/**
 * Förutsättningar-panel som visar alla värden simulationen bygger på.
 *
 * Strategi:
 *   - Klar dropdown med preset-tariffer (för start: E.on Syd default + generisk)
 *   - "Fyll i själv" alternativ som öppnar fält för manuell justering
 *   - Alla fält är synliga och justerbara, ingen black box
 */

import { useState } from "react";
import type { GridFeeConfig, Scenario } from "../sim/types";
import { DEFAULT_DEGRADATION } from "../sim/types";

interface AssumptionsPanelProps {
  scenario: Scenario;
  onChange: (scenario: Scenario) => void;
}

export default function AssumptionsPanel({
  scenario,
  onChange,
}: AssumptionsPanelProps) {
  const [expanded, setExpanded] = useState(false);

  function updateGrid(patch: Partial<GridFeeConfig>) {
    onChange({ ...scenario, grid: { ...scenario.grid, ...patch } });
  }

  function updateTier(
    field: "thresholdKWhPerYear" | "aboveFixedMonthly" | "aboveTransmission",
    value: number,
  ) {
    const current = scenario.grid.tier;
    if (!current) return;
    const next = {
      ...current,
      ...(field === "thresholdKWhPerYear" && { thresholdKWhPerYear: value }),
      ...(field === "aboveFixedMonthly" && {
        aboveTier: { ...current.aboveTier, fixedMonthlySek: value },
      }),
      ...(field === "aboveTransmission" && {
        aboveTier: { ...current.aboveTier, transmissionPerKWh: value },
      }),
    };
    onChange({
      ...scenario,
      grid: { ...scenario.grid, tier: next },
    });
  }

  const g = scenario.grid;

  return (
    <section className="assumptions">
      <header className="assumptions-header">
        <h3>Förutsättningar</h3>
        <button
          type="button"
          className="link"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? "Dölj detaljer" : "Visa detaljer"}
        </button>
      </header>

      {expanded && (
        <div className="assumption-fields">
          <h4>Elnätsavgift</h4>
          <div className="field-grid">
            <NumberField
              label="Fast avgift kr/månad (exkl moms)"
              value={g.fixedMonthlySek}
              onChange={(v) => updateGrid({ fixedMonthlySek: v })}
              step={1}
            />
            <NumberField
              label="Elöverföring kr/kWh (exkl moms)"
              value={g.transmissionPerKWh}
              onChange={(v) => updateGrid({ transmissionPerKWh: v })}
              step={0.01}
            />
            <NumberField
              label="Nätnytta utmatad kr/kWh"
              value={g.feedInCompensationPerKWh}
              onChange={(v) => updateGrid({ feedInCompensationPerKWh: v })}
              step={0.01}
            />
          </div>

          {g.tier && (
            <>
              <h4>Tröskelmodell</h4>
              <p className="hint">
                Vid årsförbrukning över tröskeln byter avgiften till "över"-
                kolumnen. Förekommer hos vissa lokalnät som Skånska Energi Nät.
              </p>
              <div className="field-grid">
                <NumberField
                  label="Tröskel kWh/år"
                  value={g.tier.thresholdKWhPerYear}
                  onChange={(v) => updateTier("thresholdKWhPerYear", v)}
                  step={100}
                />
                <NumberField
                  label="Över tröskel: fast kr/mån"
                  value={g.tier.aboveTier.fixedMonthlySek}
                  onChange={(v) => updateTier("aboveFixedMonthly", v)}
                  step={1}
                />
                <NumberField
                  label="Över tröskel: kr/kWh"
                  value={g.tier.aboveTier.transmissionPerKWh}
                  onChange={(v) => updateTier("aboveTransmission", v)}
                  step={0.01}
                />
              </div>
            </>
          )}

          <h4>Skatter</h4>
          <div className="field-grid">
            <NumberField
              label="Energiskatt kr/kWh"
              value={scenario.tax.energyTaxPerKWh}
              onChange={(v) =>
                onChange({
                  ...scenario,
                  tax: { ...scenario.tax, energyTaxPerKWh: v },
                })
              }
              step={0.001}
            />
            <NumberField
              label="Moms (andel)"
              value={scenario.tax.vatRate}
              onChange={(v) =>
                onChange({
                  ...scenario,
                  tax: { ...scenario.tax, vatRate: v },
                })
              }
              step={0.01}
            />
            <NumberField
              label="60-öringen kr/kWh"
              value={scenario.tax.microproducerCreditPerKWh}
              onChange={(v) =>
                onChange({
                  ...scenario,
                  tax: { ...scenario.tax, microproducerCreditPerKWh: v },
                })
              }
              step={0.01}
            />
          </div>
          <p className="hint">
            60-öringen försvann 2026-01-01 enligt riksdagsbeslut. Den
            tillämpas automatiskt bara på historiska timmar före det datumet.
          </p>

          <h4>Elhandel (Tibber)</h4>
          <div className="field-grid">
            <NumberField
              label="Importpåslag kr/kWh"
              value={scenario.supplier.importMarkupPerKWh}
              onChange={(v) =>
                onChange({
                  ...scenario,
                  supplier: {
                    ...scenario.supplier,
                    importMarkupPerKWh: v,
                  },
                })
              }
              step={0.01}
            />
            <NumberField
              label="Exportavdrag kr/kWh"
              value={scenario.supplier.exportMarkdownPerKWh}
              onChange={(v) =>
                onChange({
                  ...scenario,
                  supplier: {
                    ...scenario.supplier,
                    exportMarkdownPerKWh: v,
                  },
                })
              }
              step={0.01}
            />
          </div>
        </div>
      )}

      <ScenarioToggleSection scenario={scenario} onChange={onChange} />
    </section>
  );
}

/**
 * Två togglesektioner för:
 *   - Stödtjänster (FCR-D via aggregator)
 *   - Framtida effekttariff (scenario)
 *
 * Båda är "vad-om"-lager ovanpå basbesparingen. Default OFF för att
 * basresultatet ska vara fritt från optimistiska antaganden.
 */
function ScenarioToggleSection({
  scenario,
  onChange,
}: {
  scenario: Scenario;
  onChange: (scenario: Scenario) => void;
}) {
  const ancillary = scenario.ancillary;
  const powerTariff = scenario.grid.powerTariff;

  function updateAncillary(patch: Partial<Scenario["ancillary"]>) {
    onChange({ ...scenario, ancillary: { ...ancillary, ...patch } });
  }

  function updateDegradation(
    patch: Partial<NonNullable<Scenario["degradation"]>>,
  ) {
    const current = scenario.degradation ?? DEFAULT_DEGRADATION;
    onChange({
      ...scenario,
      degradation: { ...current, ...patch },
    });
  }

  function setPowerTariff(pt: Scenario["grid"]["powerTariff"] | undefined) {
    onChange({
      ...scenario,
      grid: { ...scenario.grid, powerTariff: pt },
    });
  }

  function togglePowerTariff(on: boolean) {
    if (on) {
      // Defaultvärden för scenario. activateFromYear=0 så det syns direkt
      // i resultatet — användaren kan ändra till senare år för att modellera
      // fördröjt införande.
      setPowerTariff({
        pricePerKWPerMonth: 70,
        topHoursAveraged: 3,
        seasonalDifferentiation: {
          winterMonths: [11, 12, 1, 2, 3],
          winterMultiplier: 1.5,
        },
        activateFromYear: 0,
      });
    } else {
      setPowerTariff(undefined);
    }
  }

  return (
    <>
      {/* Stödtjänster --------------------------------------------------- */}
      <section className="scenario-toggle">
        <header className="scenario-toggle-header">
          <label className="toggle-label">
            <input
              type="checkbox"
              checked={ancillary.enabled}
              onChange={(e) => updateAncillary({ enabled: e.target.checked })}
            />
            <span>Stödtjänster (FCR-D)</span>
          </label>
        </header>
        <p className="hint">
          Aggregatorer som CheckWatt, Tibber och GreenelyFlex tecknar avtal
          om att batteriets kapacitet får styras för frekvensreglering. Du får
          en intäkt per kW maxeffekt. Marknaden mättas snabbt när fler batterier
          ansluter sig så priserna sjunker över tid.
        </p>
        {ancillary.enabled && (
          <AncillaryPresetSelector
            ancillary={ancillary}
            onChange={updateAncillary}
          />
        )}
      </section>

      {/* Effekttariff scenario ----------------------------------------- */}
      <section className="scenario-toggle">
        <header className="scenario-toggle-header">
          <label className="toggle-label">
            <input
              type="checkbox"
              checked={!!powerTariff}
              onChange={(e) => togglePowerTariff(e.target.checked)}
            />
            <span>Effekttariff (scenario)</span>
          </label>
        </header>
        <p className="hint">
          Regeringen stoppade kravet på obligatorisk effekttariff den 13 mars
          2026. E.on har pausat sitt planerade införande 1 september 2026.
          Ei ska ta fram en ny modell senast 12 april 2027. Det är osäkert
          när och hur effekttariff faktiskt återinförs hos E.on.
          Denna toggle låter dig modellera ett scenario.
        </p>
        {powerTariff && (
          <>
            <p className="hint info-text">
              DP-optimeraren visar potentialen som ett batteri MED smart
              styrning kan ge. I praktiken kräver det att ditt batteri har
              ett aktivt energi-styrsystem (EMS) som ser elnätspriser och
              månadens hittills-toppar. Exempel: Tibber Smart Charging,
              CheckWatt, Sungrow EMS, Huawei FusionHome med Smart Energy
              Optimizer. Ett batteri som bara lagrar solöverskott och
              urladdar mot import kommer INTE att kapa topp-timmar
              automatiskt och ger inte denna besparing.
            </p>
            {scenario.strategy !== "optimal-foresight" && (
              <p
                className="hint info-text"
                style={{
                  borderLeft: "3px solid var(--accent)",
                  paddingLeft: "0.6rem",
                  background: "rgba(125, 194, 29, 0.08)",
                }}
              >
                Din valda strategi är heuristisk och saknar aktiv
                peak-shaving. Den kan till och med skapa nya toppar genom
                att ladda batteriet när priserna är låga. Det förklarar
                varför besparingen kan SJUNKA när du aktiverar
                effekttariff. För att se vad ett peak-tariff-medvetet EMS
                skulle ge, byt strategi till <strong>Optimal</strong>.
                Verkliga kommersiella system når typiskt 70-85% av
                Optimal-siffran.
              </p>
            )}
            <div className="fields">
              <NumberFieldWithInfo
                label="Pris per kW och månad (kr)"
                value={powerTariff.pricePerKWPerMonth}
                onChange={(v) =>
                  setPowerTariff({ ...powerTariff, pricePerKWPerMonth: v })
                }
                step={5}
                info="Bergs Tingslags 2026: 159 kr/kW/månad (Sveriges högsta). Vattenfalls planerade modell: ~50-70 kr/kW/månad. Ellevios var ~63 kr. Default 70 är medelhögt scenario."
              />
              <NumberFieldWithInfo
                label="Antal toppar att medelvärdes"
                value={powerTariff.topHoursAveraged}
                onChange={(v) =>
                  setPowerTariff({
                    ...powerTariff,
                    topHoursAveraged: Math.max(1, Math.round(v)),
                  })
                }
                step={1}
                info="Hur många av månadens högsta timmedeleffekter som snittas. 1 = bara den absolut högsta. 3 = snitt av tre högsta (vanligast). Ei rekommenderade 3 i sin föreskrift."
              />
              <NumberFieldWithInfo
                label="Vintermultiplikator"
                value={powerTariff.seasonalDifferentiation?.winterMultiplier ?? 1}
                onChange={(v) =>
                  setPowerTariff({
                    ...powerTariff,
                    seasonalDifferentiation: {
                      winterMonths: [11, 12, 1, 2, 3],
                      winterMultiplier: v,
                    },
                  })
                }
                step={0.1}
                info="Många nätbolag dyrare under vinter (nov-mars). 1.0 = ingen säsongsdifferens. 1.5 = vintern kostar 50% mer. Sätt 1.0 om du inte vill ha säsongsdifferens."
              />
              <NumberFieldWithInfo
                label="Aktiveras från år"
                value={powerTariff.activateFromYear ?? 0}
                onChange={(v) =>
                  setPowerTariff({
                    ...powerTariff,
                    activateFromYear: Math.max(0, Math.round(v)),
                  })
                }
                step={1}
                info="0 = aktiv från år 1 (default, syns direkt i siffran ovan). Sätt 2 för att modellera fördröjt införande där tariffen börjar gälla först år 3. Tariffen räknas helt enkelt inte med innevarande år om värdet är större än 0."
              />
            </div>
          </>
        )}
      </section>

      {/* Batterilivslängd ----------------------------------------------- */}
      <section className="scenario-toggle">
        <header className="scenario-toggle-header">
          <span className="scenario-toggle-title">Batterilivslängd</span>
        </header>
        <p className="hint">
          Antaganden om hur länge batteriet håller och hur snabbt kapaciteten
          minskar. Påverkar inte besparingen år 1 men färgkodar
          återbetalningstiden mot förväntad livslängd.
        </p>
        <DegradationPresetSelector
          degradation={scenario.degradation ?? DEFAULT_DEGRADATION}
          onChange={updateDegradation}
        />
      </section>
    </>
  );
}

/**
 * Tre snabbknappar för batterilivslängd: Pessimistisk, Neutral, Optimistisk.
 * Sätter förväntad livslängd och årlig kapacitetstapp samtidigt.
 */
function DegradationPresetSelector({
  degradation,
  onChange,
}: {
  degradation: NonNullable<Scenario["degradation"]>;
  onChange: (patch: Partial<NonNullable<Scenario["degradation"]>>) => void;
}) {
  type PresetKey = "cautious" | "neutral" | "optimistic";
  interface Preset {
    key: PresetKey;
    label: string;
    description: string;
    values: NonNullable<Scenario["degradation"]>;
  }

  const PRESETS: Preset[] = [
    {
      key: "cautious",
      label: "Försiktig",
      description:
        "10 års livslängd, 2.5% kapacitetstapp per år. Hård cykling, kall installation.",
      values: { expectedLifetimeYears: 10, annualCapacityLossRate: 0.025 },
    },
    {
      key: "neutral",
      label: "Neutral",
      description:
        "12 års livslängd, 2% kapacitetstapp per år. Typiskt LFP-värden med måttlig cykling.",
      values: { expectedLifetimeYears: 12, annualCapacityLossRate: 0.02 },
    },
    {
      key: "optimistic",
      label: "Optimistisk",
      description:
        "15 års livslängd, 1.5% kapacitetstapp per år. Försiktig cykling, temperaturkontrollerad installation.",
      values: { expectedLifetimeYears: 15, annualCapacityLossRate: 0.015 },
    },
  ];

  function classifyCurrent(): PresetKey | null {
    for (const p of PRESETS) {
      if (
        p.values.expectedLifetimeYears === degradation.expectedLifetimeYears &&
        Math.abs(
          p.values.annualCapacityLossRate - degradation.annualCapacityLossRate,
        ) < 0.003
      ) {
        return p.key;
      }
    }
    return null;
  }
  const activeKey = classifyCurrent();

  return (
    <>
      <div className="preset-buttons">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            className={`preset-button ${activeKey === p.key ? "active" : ""}`}
            onClick={() => onChange(p.values)}
          >
            {p.label}
          </button>
        ))}
      </div>
      <p className="hint preset-description">
        {PRESETS.find((p) => p.key === (activeKey ?? "neutral"))!.description}
      </p>
    </>
  );
}

/**
 * Tre snabbknappar för stödtjänster: Försiktig, Neutral, Optimistisk.
 * Sätter alla 5 parametrar (intäkt, aggregator, prismultiplikator, horisont,
 * reservation) samtidigt. Användaren kan fortfarande finjustera via
 * "Visa detaljer".
 */
function AncillaryPresetSelector({
  ancillary,
  onChange,
}: {
  ancillary: Scenario["ancillary"];
  onChange: (patch: Partial<Scenario["ancillary"]>) => void;
}) {
  const [showDetails, setShowDetails] = useState(false);

  type PresetKey = "cautious" | "neutral" | "optimistic";
  interface Preset {
    key: PresetKey;
    label: string;
    description: string;
    values: Omit<Scenario["ancillary"], "enabled">;
  }

  const PRESETS: Preset[] = [
    {
      key: "cautious",
      label: "Försiktig",
      description:
        "Marknaden mättas snabbt, kort horisont, mer kapacitet reserverad.",
      values: {
        annualRevenuePerKW: 1000,
        aggregatorShare: 0.3,
        annualPriceMultiplier: 0.8,
        horizonYears: 3,
        reservedCapacityFraction: 0.3,
      },
    },
    {
      key: "neutral",
      label: "Neutral",
      description:
        "Branschmedel-antaganden. Ungefär dagens FCR-marknad fortsätter med svag nedgång.",
      values: {
        annualRevenuePerKW: 1500,
        aggregatorShare: 0.25,
        annualPriceMultiplier: 0.85,
        horizonYears: 5,
        reservedCapacityFraction: 0.25,
      },
    },
    {
      key: "optimistic",
      label: "Optimistisk",
      description:
        "Marknaden håller sig nära historiska nivåer, längre horisont.",
      values: {
        annualRevenuePerKW: 2000,
        aggregatorShare: 0.2,
        annualPriceMultiplier: 0.92,
        horizonYears: 7,
        reservedCapacityFraction: 0.2,
      },
    },
  ];

  // Vilken preset matchar aktuella värden? (Klassificera närmast)
  function classifyCurrent(): PresetKey | null {
    for (const p of PRESETS) {
      if (
        Math.abs(p.values.annualRevenuePerKW - ancillary.annualRevenuePerKW) <
          50 &&
        Math.abs(p.values.aggregatorShare - ancillary.aggregatorShare) <
          0.02 &&
        Math.abs(
          p.values.annualPriceMultiplier - ancillary.annualPriceMultiplier,
        ) < 0.02 &&
        p.values.horizonYears === ancillary.horizonYears &&
        Math.abs(
          p.values.reservedCapacityFraction -
            ancillary.reservedCapacityFraction,
        ) < 0.02
      ) {
        return p.key;
      }
    }
    return null;
  }
  const activeKey = classifyCurrent();

  return (
    <>
      <div className="preset-buttons">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            className={`preset-button ${activeKey === p.key ? "active" : ""}`}
            onClick={() => onChange(p.values)}
          >
            {p.label}
          </button>
        ))}
      </div>
      <p className="hint preset-description">
        {PRESETS.find((p) => p.key === (activeKey ?? "neutral"))!.description}
      </p>

      <button
        type="button"
        className="details-toggle"
        onClick={() => setShowDetails(!showDetails)}
      >
        {showDetails ? "Dölj parametrar" : "Visa parametrar"}
      </button>

      {showDetails && (
        <div className="fields">
          <NumberFieldWithInfo
            label="Intäkt per kW maxeffekt år 1 (kr/kW/år)"
            value={ancillary.annualRevenuePerKW}
            onChange={(v) => onChange({ annualRevenuePerKW: v })}
            step={100}
            info="CheckWatts kundredovisning 2024: median ~1850 kr/kW/år. Trenden är fallande. Vid 2026 är ett konservativt antagande 1000-1500 kr/kW/år."
          />
          <NumberFieldWithInfo
            label="Aggregatorns andel (0-1)"
            value={ancillary.aggregatorShare}
            onChange={(v) =>
              onChange({ aggregatorShare: Math.min(1, Math.max(0, v)) })
            }
            step={0.05}
            info="CheckWatt: ~20%. Tibber: ~30%. GreenelyFlex: ~25-35%. Anges som decimaltal."
          />
          <NumberFieldWithInfo
            label="Årlig prismultiplikator"
            value={ancillary.annualPriceMultiplier}
            onChange={(v) => onChange({ annualPriceMultiplier: v })}
            step={0.05}
            info="0.85 betyder att marknadspriset sjunker 15% per år. FCR-D-marknaden har redan halverats från toppen 2022-2023."
          />
          <NumberFieldWithInfo
            label="Tidshorisont (år)"
            value={ancillary.horizonYears}
            onChange={(v) =>
              onChange({ horizonYears: Math.max(1, Math.round(v)) })
            }
            step={1}
            info="Hur många år vi snittar intäkten över. Bortom dess är priserna så osäkra att vi inte räknar med intäkt."
          />
          <NumberFieldWithInfo
            label="Reserverad batterikapacitet (0-1)"
            value={ancillary.reservedCapacityFraction}
            onChange={(v) =>
              onChange({
                reservedCapacityFraction: Math.min(0.5, Math.max(0, v)),
              })
            }
            step={0.05}
            info="Andel av batteriet som måste hållas tillgängligt för FCR (kan inte användas för arbitrage). Typiskt 20-30%."
          />
        </div>
      )}
    </>
  );
}

function NumberFieldWithInfo({
  label,
  value,
  onChange,
  step,
  info,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  step: number;
  info: string;
}) {
  return (
    <label className="number-field-with-info">
      <span className="field-label">
        {label}
        <InfoButton text={info} />
      </span>
      <input
        type="number"
        value={value}
        step={step}
        onChange={(e) => {
          const v = parseFloat(e.target.value);
          if (!Number.isNaN(v)) onChange(v);
        }}
      />
    </label>
  );
}

function InfoButton({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="info-wrapper">
      <button
        type="button"
        className="info-button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Mer information"
      >
        ⓘ
      </button>
      {open && (
        <span className="info-tooltip">
          {text}
        </span>
      )}
    </span>
  );
}

function NumberField({
  label,
  value,
  onChange,
  step,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  step: number;
}) {
  return (
    <label>
      <span>{label}</span>
      <input
        type="number"
        value={value}
        step={step}
        onChange={(e) => {
          const v = parseFloat(e.target.value);
          if (!Number.isNaN(v)) onChange(v);
        }}
      />
    </label>
  );
}
